"use client";

/**
 * Venue Renderer
 * Renders venue component preview in the canvas
 */

import type React from "react";
import dynamic from "next/dynamic";
import { MapPin } from "lucide-react";
import { getButtonTextColor } from "@/features/landing-pages/_logic/color-utils";
import type { VenueComponent } from "../../../_types";
import { useBuilderContext } from "../../../_hooks/useBuilderContext";
import { useCanvasImageClick } from "../../../_hooks/useCanvasImageClick";
import { useCanvasImageUpload } from "../../../_hooks/useCanvasImageUpload";
import { VENUE_MAP_HEIGHT_PX } from "../../../_logic/constants";
import { buildVenueMapsUrl, isLikelyAddress } from "../../../_logic/venue-address";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { ImageCarousel } from "../../public/ImageCarousel";
import { SmartButton } from "../../public/SmartButton";
import { ImageCarouselPreview } from "./_shared/ImageCarouselPreview";
import { InlineTextEdit } from "./_shared/InlineTextEdit";
import { InlineTipTapEdit } from "./_shared/InlineTipTapEdit";
import { SectionContainer } from "./_shared/SectionContainer";
import { SECTION_PROSE_CLASSES } from "./_shared/prose-section-text";

// Code-split the Google Maps SDK: it's only pulled in when a venue is actually in map mode,
// keeping the heavy `@react-google-maps/api` bundle out of every other landing page.
const VenueMap = dynamic(() => import("./_shared/VenueMap").then((m) => m.VenueMap), {
  ssr: false,
  loading: () => (
    <div
      className="animate-pulse rounded-xl bg-gray-100"
      style={{ height: `${VENUE_MAP_HEIGHT_PX}px` }}
      aria-busy="true"
    />
  ),
});

interface VenueRendererProps {
  component: VenueComponent;
}

/**
 * Renders a venue section preview.
 *
 * The media column shows either an image carousel or a static map of the
 * address depending on `mediaMode`. On the public page the column is omitted
 * entirely when there is nothing to show (image mode with no images, or map
 * mode with no address), so the details span the full width. The address links
 * out to Google Maps on the public page when it looks like a real address.
 *
 * @param props.component - The venue component data to render.
 * @returns The rendered venue section.
 */
export function VenueRenderer({ component }: VenueRendererProps): React.ReactElement {
  const { title, address, description, buttonText, buttonLink, images, mediaMode } = component;
  const { primaryColor, eventId } = useBuilderContext();
  const { isPublic } = usePublicViewContext();
  const { handleImageClick } = useCanvasImageClick(component.id, "venue");
  const handleFilesSelected = useCanvasImageUpload(component.id, images);
  const buttonTextColor = getButtonTextColor(primaryColor);

  // Derived values
  const trimmedAddress = address?.trim() ?? "";
  const isMapMode = mediaMode === "map";
  const addressIsLinkable = isPublic && isLikelyAddress(address);

  // Decide what fills the media column. `null` means no column at all.
  let media: React.ReactNode = null;
  if (isMapMode) {
    if (trimmedAddress) {
      media = <VenueMap address={trimmedAddress} />;
    } else if (!isPublic) {
      media = (
        <div className="flex h-full min-h-[200px] items-center justify-center rounded-xl bg-gray-100 p-6 text-center text-sm font-medium text-gray-500">
          Add an address to show the map
        </div>
      );
    }
  } else if (isPublic) {
    media = images && images.length > 0 ? <ImageCarousel images={images} /> : null;
  } else {
    media = (
      <ImageCarouselPreview images={images} onImageClick={handleImageClick} onFilesSelected={handleFilesSelected} />
    );
  }

  const hasMediaColumn = media !== null;
  const twoColClass = isPublic ? "md:grid-cols-[2fr_1fr] md:gap-10" : "@md:grid-cols-[2fr_1fr] @md:gap-10";
  const layoutClass = hasMediaColumn ? twoColClass : "";

  return (
    <SectionContainer
      maxWidth="5xl"
      background={component.background}
      title={title || ""}
      componentId={component.id}
      titleFieldId="venue-title"
    >
      <div className={`grid gap-8 ${layoutClass}`}>
        {/* Media: image carousel or static map */}
        {hasMediaColumn && <div className="relative overflow-hidden rounded-xl">{media}</div>}

        {/* Details */}
        <div className="flex flex-col justify-center gap-4">
          {(!isPublic || address) && (
            <p className="text-lg font-semibold text-[color:var(--lp-text,#374151)]">
              <MapPin
                className="mr-2 inline-block h-5 w-5 align-[-4px] text-[color:var(--lp-text,#6b7280)]"
                aria-hidden="true"
              />
              {addressIsLinkable ? (
                <a
                  href={buildVenueMapsUrl(address as string)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-semibold text-[color:var(--lp-text,#374151)] underline-offset-2 hover:underline"
                >
                  {address}
                </a>
              ) : (
                <InlineTextEdit
                  tag="span"
                  value={address || ""}
                  placeholder="Add venue address..."
                  className="text-lg font-semibold text-[color:var(--lp-text,#374151)]"
                  componentId={component.id}
                  fieldId="venue-address"
                  field="address"
                />
              )}
            </p>
          )}

          <InlineTipTapEdit
            content={description || ""}
            className={`prose-base ${SECTION_PROSE_CLASSES}`}
            componentId={component.id}
            fieldId="venue-description"
            field="description"
            placeholder="Add venue description..."
          />

          {component.showButton &&
            buttonText &&
            (isPublic && buttonLink ? (
              <SmartButton
                href={buttonLink}
                label={buttonText}
                primaryColor={primaryColor}
                eventId={eventId}
                className="inline-flex w-fit items-center rounded-xl px-5 py-2.5 text-base font-semibold transition-opacity hover:opacity-90"
                style={{ backgroundColor: primaryColor, color: buttonTextColor }}
              />
            ) : (
              <span
                className="inline-block w-fit rounded-xl px-5 py-2.5 text-base font-semibold"
                style={{ backgroundColor: primaryColor, color: buttonTextColor }}
              >
                {buttonText}
              </span>
            ))}
        </div>
      </div>
    </SectionContainer>
  );
}
